import { useEffect, useMemo, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTranslation } from 'react-i18next';
import { detectLanguage } from '../../utils/languageDetection';
import './CodeBlock.css';

/**
 * A syntax-highlighted code block with a copy control.
 *
 * Props are unchanged (`code`, `language`, `filename`). What changed:
 *   - the chrome is drawn from semantic tokens and the highlighting theme
 *     follows the active Etch theme, so a code block on Paper is no longer a
 *     dark rectangle in a light page,
 *   - the code scrolls *inside its own box*: long lines never widen the chat
 *     column or the source panel (spec §15.3 — no horizontal page scroll),
 *   - the scroll container is focusable, because a scrollable region that
 *     cannot be reached by keyboard fails WCAG 2.2 (spec §12).
 */
export function CodeBlock({ code, language = '', filename = '' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const theme = useDocumentTheme();

  // Auto-detect language if not explicitly provided
  const detectedLanguage = useMemo(
    () => detectLanguage(code, language, filename),
    [code, language, filename]
  );

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const label = filename || detectedLanguage;

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-language">{label}</span>
        <button
          type="button"
          className="copy-button"
          onClick={handleCopy}
          aria-label={t('common.copyCode')}
        >
          {copied ? t('common.copied') : t('common.copy')}
        </button>
      </div>
      <div className="code-block-scroll" tabIndex={0} role="group" aria-label={label}>
        <SyntaxHighlighter
          language={detectedLanguage}
          style={theme === 'paper' ? oneLight : oneDark}
          customStyle={{
            margin: 0,
            background: 'transparent',
            borderRadius: 0,
            fontSize: 'var(--text-sm)'
          }}
          codeTagProps={{ style: { fontFamily: 'var(--font-mono)' } }}
          showLineNumbers={code.split('\n').length > 3}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

/**
 * The resolved theme, read off `<html data-theme>` (ThemeContext owns that
 * attribute). Deliberately not `useTheme()`: code blocks also render inside
 * previews and error fallbacks that may sit outside the provider, and a
 * missing provider must not throw in a component this widely used.
 */
function useDocumentTheme() {
  const read = () =>
    typeof document === 'undefined' ? 'ink' : document.documentElement.dataset.theme || 'ink';

  const [theme, setTheme] = useState(read);

  useEffect(() => {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return undefined;
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    setTheme(read());
    return () => observer.disconnect();
  }, []);

  return theme;
}

export default CodeBlock;
